import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

// "What's new" — shown once per device after logging in to a new version.
// Bump WHATS_NEW_VERSION (and the text) for the next big update.
export const WHATS_NEW_VERSION = '2026-10-major';
const KEY = 'whatsNewSeen';

// DEV ONLY: localhost/?whatsnew shows the window again without resetting anything
export const isWhatsNewPreview = () => process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('whatsnew');

export const shouldShowWhatsNew = () => {
  if (isWhatsNewPreview()) return true;
  try { return localStorage.getItem(KEY) !== WHATS_NEW_VERSION; } catch (e) { return false; }
};
// Major reset: the first time this device opens the new version, forget its saved settings
// (favourite teams, theme, collectables, vibrations…). ONLY the language is kept.
// The login session is not touched. Runs before the app renders (see index.js).
const DEVICE_SETTINGS = ['favoriteNHL', 'favoriteNBA', 'favoriteMLB', 'themeMode', 'themeSource',
  'collectablesEnabled', 'cardProtection', 'hapticsEnabled', 'tickerEnabled'];
export function resetDeviceSettingsForNewVersion() {
  try {
    if (isWhatsNewPreview() || localStorage.getItem(KEY) === WHATS_NEW_VERSION) return;
    DEVICE_SETTINGS.forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* private mode */ }
}
const markSeen = () => { if (isWhatsNewPreview()) return; try { localStorage.setItem(KEY, WHATS_NEW_VERSION); } catch (e) { /* private mode */ } };

// last season's podium: saved by the reset (season_awards). Before the reset exists (localhost
// preview) it falls back to today's top 3 with the same prizes, so the design can be checked.
const PRIZES = [750, 500, 250];
function usePodium() {
  const [podium, setPodium] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await supabase.from('season_awards').select('place, username, final_balance, prize')
        .eq('season', '2025-26').order('place', { ascending: true });
      if (!error && data && data.length) { if (alive) setPodium(data); return; }
      const { data: top } = await supabase.from('profiles').select('username, balance').not('username', 'like', 'CPU-%').order('balance', { ascending: false }).limit(3);
      if (alive && top?.length) setPodium(top.map((p, i) => ({ place: i + 1, username: p.username, final_balance: p.balance, prize: PRIZES[i] })));
    })();
    return () => { alive = false; };
  }, []);
  return podium;
}

export default function WhatsNew({ lang = 'en', onClose }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const close = () => { markSeen(); onClose?.(); };
  const podium = usePodium();
  const byPlace = (n) => podium?.find((p) => p.place === n);
  return (
    <div className="wn-backdrop">
      <div className="wn-modal" role="dialog" aria-modal="true" aria-labelledby="wn-title">
        <div className="wn-head">
          <span className="wn-kicker">{t.kicker}</span>
          <h2 id="wn-title" className="wn-title">{t.title}</h2>
          <p className="wn-sub">{t.sub}</p>
        </div>

        <div className="wn-reset">
          <b className="wn-reset-amount">$1,000</b>
          <span>{t.reset}</span>
        </div>

        {podium && podium.length > 0 && (
          <div className="wn-podium-wrap">
            <span className="wn-podium-title">{t.podiumTitle}</span>
            <div className="wn-podium">
              {[2, 1, 3].map((n) => {
                const p = byPlace(n);
                if (!p) return <div key={n} />;
                return (
                  <div key={n} className={`wn-step place-${n}`}>
                    <span className="wn-step-medal">{n === 1 ? '🥇' : n === 2 ? '🥈' : '🥉'}</span>
                    <span className="wn-step-name">{p.username}</span>
                    <span className="wn-step-block">
                      <b>+{lang === 'fr' ? `${Number(p.prize)} $` : `$${Number(p.prize)}`}</b>
                      <small>{t.bonus}</small>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="wn-list">
          {t.items.map(([title, text]) => (
            <div key={title} className="wn-item">
              <b>{title}</b>
              <span>{text}</span>
            </div>
          ))}
        </div>

        <button type="button" className="wn-go" onClick={close}>{t.go}</button>
      </div>
    </div>
  );
}

const TR = {
  en: {
    kicker: 'Major update',
    title: 'What’s new on BarDownBets',
    sub: 'A whole new look, and a brand new way to play pools.',
    podiumTitle: 'Last season’s podium', bonus: 'starting bonus',
    reset: 'Fresh start: every bet was cleared and every account is back to $1,000 (last season’s top 3 start with a bonus). LOSER tags, mulligans, favourite teams and themes are reset too, so pick your team again in the menu. Good luck!',
    items: [
      ['New look', 'A cleaner design everywhere, still in your team’s colours. Smoother on phones and tablets.'],
      ['Pools, rebuilt', 'A live draft room, a reaction game to set the draft order, and you draft your own NHL team first. It becomes your identity and scores standings points.'],
      ['Lineup mode', 'Set your lines every day: Line 1 and Pair 1 count double, extras score 0.'],
      ['Free agency', 'Drop a player and sign an undrafted one, 2 pickups a month.'],
      ['Salary cap', 'Real NHL contracts. Play with a hard cap, or go over and take the penalties.'],
      ['Injuries in every game', 'Open a game to see who’s out for both teams, right where you bet.'],
      ['Little extras', 'Game counts on the filters, a new leaderboard, smoother animations and light vibrations on phones.'],
    ],
    go: 'Let’s go',
  },
  fr: {
    kicker: 'Grosse mise à jour',
    title: 'Quoi de neuf sur BarDownBets',
    sub: 'Un tout nouveau look et une toute nouvelle façon de jouer aux pools.',
    podiumTitle: 'Le podium de la saison dernière', bonus: 'bonus de départ',
    reset: 'On repart à zéro : tous les paris ont été effacés et chaque compte revient à 1 000 $ (le top 3 de la saison dernière part avec un bonus). Les étiquettes LOSER, les mulligans, les équipes favorites et les thèmes sont remis à zéro aussi : choisis ton équipe à nouveau dans le menu. Bonne chance!',
    items: [
      ['Nouveau look', 'Un design plus épuré partout, toujours aux couleurs de ton équipe. Plus fluide sur téléphone et tablette.'],
      ['Pools, refaits', 'Une salle de repêchage en direct, un jeu de réflexes pour l’ordre du repêchage, et tu repêches d’abord ta propre équipe LNH. Elle devient ton identité et marque ses points au classement.'],
      ['Mode alignement', 'Place tes trios chaque jour : le 1er trio et la 1re paire comptent double, les réserves ne marquent pas.'],
      ['Joueurs autonomes', 'Libère un joueur et signe un joueur non repêché, 2 signatures par mois.'],
      ['Plafond salarial', 'Les vrais contrats de la LNH. Joue avec un plafond strict, ou dépasse et subis les pénalités.'],
      ['Blessures dans chaque match', 'Ouvre un match pour voir qui est absent des deux côtés, là où tu paries.'],
      ['Petits extras', 'Le nombre de matchs sur les filtres, un nouveau classement, des animations plus fluides et de petites vibrations sur téléphone.'],
    ],
    go: 'C’est parti',
  },
};
